import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ThemeToggle from '../common/ThemeToggle';

interface NavGroup {
  title?: string;
  items: { path: string; label: string; icon: string }[];
}

const navGroups: NavGroup[] = [
  {
    title: '主要功能',
    items: [
      { path: '/dashboard', label: '儀表板', icon: '📊' },
      { path: '/playground', label: '測試區', icon: '🎮' },
      { path: '/logs', label: '請求紀錄', icon: '📋' },
    ],
  },
  {
    title: '金鑰管理',
    items: [
      { path: '/api-keys', label: 'API 金鑰', icon: '🔑' },
    ],
  },
  {
    title: '其他',
    items: [
      { path: '/docs', label: '使用指南', icon: '📖' },
      { path: '/profile', label: '個人資料', icon: '👤' },
    ],
  },
];

const adminNavGroups: NavGroup[] = [
  {
    title: '用戶與權限',
    items: [
      { path: '/users', label: '用戶管理', icon: '👥' },
    ],
  },
  {
    title: '系統資源',
    items: [
      { path: '/jina-keys', label: 'Jina 金鑰', icon: '🗝️' },
      { path: '/settings', label: '系統設定', icon: '⚙️' },
    ],
  },
];

interface SidebarProps {
  onClose?: () => void;
}

export default function Sidebar({ onClose }: SidebarProps) {
  const { user, logout, isAdmin } = useAuth();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    // Default all groups to expanded
    const initial: Record<string, boolean> = {};
    navGroups.forEach(g => { if (g.title) initial[g.title] = true; });
    adminNavGroups.forEach(g => { if (g.title) initial[g.title] = true; });
    return initial;
  });

  const handleNavClick = () => {
    onClose?.();
  };

  const toggleGroup = (title: string) => {
    setExpandedGroups(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const renderNavGroup = (group: NavGroup, index: number) => {
    const isExpanded = group.title ? expandedGroups[group.title] !== false : true;
    
    return (
      <div key={group.title || index} className="mb-2">
        {group.title && (
          <button
            onClick={() => toggleGroup(group.title!)}
            className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <span>{group.title}</span>
            <svg
              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
        {isExpanded && (
          <div className="space-y-1 mt-1">
            {group.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`
                }
              >
                <span className="mr-3">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className="h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
      {/* Logo */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h1 className="text-xl font-bold text-primary-600 dark:text-primary-400">
          大豐環保爬蟲工具
        </h1>
        <button
          onClick={onClose}
          className="lg:hidden p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        {navGroups.map((group, index) => renderNavGroup(group, index))}

        {isAdmin && (
          <>
            <div className="pt-4 pb-2 border-t border-gray-200 dark:border-gray-700 mt-4">
              <p className="px-3 text-xs font-semibold text-red-500 dark:text-red-400 uppercase tracking-wider">
                管理員專區
              </p>
            </div>
            {adminNavGroups.map((group, index) => renderNavGroup(group, index))}
          </>
        )}
      </nav>

      {/* User info and theme toggle */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">主題</span>
          <ThemeToggle />
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center min-w-0">
            <div className="w-8 h-8 rounded-full bg-primary-500 flex-shrink-0 flex items-center justify-center text-white text-sm font-medium">
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="ml-2 min-w-0">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                {user?.username}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {isAdmin ? '管理員' : '用戶'}
              </p>
            </div>
          </div>
          <button
            onClick={logout}
            className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 flex-shrink-0"
          >
            登出
          </button>
        </div>
      </div>
    </aside>
  );
}
